import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  Paper,
  SnackbarContent,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useEffect, useState } from "react";
import { PageGroupEndPointLogic } from "../../../../../logic/AssetManagement/PageGroups/pageGroupEndPointLogic";
import { useSelector } from "react-redux";
import { selectUserToken } from "../../../../../features/userSlice";

interface DuplicatePageGroupModalProps {
  open: boolean;
  handleClose: () => void;
  singleTableData: any;
  selectedSiteUid: any;
  setTableRefresh: any;
  duplicatePageGroupData: any;
  setDuplicatePageGroupData: any;
  setSnackbarOpen: any;
}

function DuplicatePageGroupModal({
  open,
  handleClose,
  singleTableData,
  setTableRefresh,
  duplicatePageGroupData,
  setDuplicatePageGroupData,
  setSnackbarOpen,
}: DuplicatePageGroupModalProps) {
  const [groupName, setGroupName] = useState("");
  const [isDuplicateButtonDisabled, setIsDuplicateButtonDisabled] =
    useState(true);
  const [data, setData] = useState<any>([]);
  const authToken = useSelector(selectUserToken);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [duplicate, setDuplicate] = useState(false);
  const { selectedSharedUid } = useSelector((state: any) => state.pageGroup);
  useEffect(() => {
    const themeSharedUids =
      singleTableData?.themes
        ?.map((theme: any) => theme.shared_uid)
        ?.filter((uid: any) => uid != null) || [];

    const popupSharedUids =
      singleTableData?.popups
        ?.map((popup: any) => popup.shared_uid)
        ?.filter((uid: any) => uid != null) || [];

    const comparisonTableShareUids =
      singleTableData?.comparison_tables
        ?.map((comparisonTable: any) => comparisonTable.shared_uid)
        ?.filter((uid: any) => uid != null) || [];

    const trafficSourceShareUids =
      singleTableData?.traffic_sources
        ?.map((trafficSource: any) => trafficSource.shared_uid)
        ?.filter((uid: any) => uid != null) || [];

    setData({
      group_name: groupName,
      headlines: singleTableData?.headlines,
      banners: singleTableData?.banners,
      theme: themeSharedUids,
      popup: popupSharedUids,
      comparison_tables: comparisonTableShareUids,
      traffic_sources: trafficSourceShareUids,
    });
    setIsDuplicateButtonDisabled(groupName.length < 8);

    // eslint-disable-next-line
  }, [open === true, groupName]);

  useEffect(() => {
    setGroupName(`${singleTableData?.group_name}-copy`);
    // eslint-disable-next-line
  }, [open === true]);

  useEffect(() => {
    const duplicatePageGroup = async () => {
      setDuplicateLoading(true);
      try {
        await PageGroupEndPointLogic.createPageGroup(
          authToken,
          selectedSharedUid,
          setDuplicatePageGroupData,
          data
        ).then(() => {
          setDuplicateLoading(false);
        });
      } catch (error) {
        console.error("Error creating page group:", error);
      }
    };

    if (duplicate === true) {
      duplicatePageGroup();
      setDuplicate(false);
    }
    // eslint-disable-next-line
  }, [duplicate === true]);

  useEffect(() => {
    if (duplicatePageGroupData.length > 0) {
      handleClose();
      setSnackbarOpen(true);
      setTableRefresh(true);
      setDuplicatePageGroupData([]);
    }
    // eslint-disable-next-line
  }, [duplicatePageGroupData.length > 0]);

  // Handle input change and validate the minimum 8 character condition
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGroupName(value);
    setIsDuplicateButtonDisabled(value.length < 8); // Enable if 8+ characters
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      PaperProps={{ sx: { width: "900px", maxWidth: "900px" } }}
    >
      <Paper sx={{ padding: "16px 24px 20px 24px" }}>
        <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
          Duplicate Page Group
        </DialogTitle>
        <TextField
          id="outlined-multiline-flexible"
          label="New Group Name" // Set your label here
          placeholder="How you like the group to be called" // Set your placeholder here
          multiline
          required
          color="secondary"
          size="small"
          maxRows={4}
          value={groupName}
          sx={{
            width: "852px",
          }}
          onChange={handleInputChange} // Update state on change
        />
        <Typography
          sx={{ fontSize: "12px", marginTop: "8px", color: "#00000099" }}
        >
          minimum of 8 characters, only letters and numbers.
        </Typography>
        <SnackbarContent
          sx={{
            background: "#FFF4E5",
            color: "#ED6C02",
            marginTop: "16px",
            boxShadow: "none",
            border: "1px solid #D32F2F80",
            padding: "0 16px",
          }}
          message={
            <Box display="flex">
              <ErrorOutlineIcon sx={{ marginRight: "8px", color: "#ED6C02" }} />
              <Box>
                <Typography
                  variant="body1"
                  fontWeight="500"
                  sx={{ marginBottom: "4px" }}
                >
                  PLEASE NOTE
                </Typography>
                <Typography variant="body2">
                  Duplicating a page group with an active A/B test will result
                  in only the first version of the theme and popup being copied.
                </Typography>
              </Box>
            </Box>
          }
        />
        <Stack flexDirection={"row"} justifyContent={"end"} marginTop={2}>
          <Button color="secondary" onClick={handleClose}>
            CANCEL
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={isDuplicateButtonDisabled || duplicateLoading === true}
            onClick={() => setDuplicate(true)}
          >
            <Stack flexDirection={"row"} gap={1} data-tracker="33">
              {duplicateLoading ? (
                <>
                  Duplicate <CircularProgress size={20} color="inherit" />
                </>
              ) : (
                <>
                  Duplicate <ContentCopyIcon />
                </>
              )}
            </Stack>
          </Button>
        </Stack>
      </Paper>
    </Dialog>
  );
}

export default DuplicatePageGroupModal;
